import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../shared/api/api-context.js";
import { requireData } from "../../shared/api/api-error.js";
import type { components } from "../../shared/api/generated/trading-engine.js";

export type MonitorInput = components["schemas"]["AiAnalysisMonitorInput"];
export type AnalysisMonitor = components["schemas"]["AiAnalysisMonitor"];
export type AnalysisReport = components["schemas"]["AiAnalysisReport"];
const key = ["ai-analysis"] as const;

export function useAnalysisStatus() {
  const api = useApi();
  return useQuery({
    queryKey: [...key, "status"],
    queryFn: async ({ signal }) => requireData(await api.GET("/ai-analysis/status", { signal })),
    refetchInterval: 15_000,
  });
}
export function useAnalysisMonitors(includeDeleted = false) {
  const api = useApi();
  return useQuery({
    queryKey: [...key, "monitors", includeDeleted],
    queryFn: async ({ signal }) =>
      requireData(
        await api.GET("/ai-analysis/monitors", { signal, params: { query: { includeDeleted } } }),
      ),
    refetchInterval: 10_000,
  });
}
export function useAnalysisHistory(monitorId: string, cursor: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...key, "history", monitorId, cursor],
    enabled: monitorId !== "",
    queryFn: async ({ signal }) =>
      requireData(
        await api.GET("/ai-analysis/monitors/{monitorId}/reports", {
          params: { path: { monitorId }, query: cursor === "" ? {} : { cursor } },
          signal,
        }),
      ),
    refetchInterval: 10_000,
  });
}
export function useAnalysisReport(reportId: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...key, "report", reportId],
    enabled: reportId !== "",
    queryFn: async ({ signal }) =>
      requireData(
        await api.GET("/ai-analysis/reports/{reportId}", {
          params: { path: { reportId } },
          signal,
        }),
      ),
    refetchInterval: (query) =>
      query.state.data?.status === "queued" || query.state.data?.status === "running"
        ? 5000
        : false,
  });
}
export function useAnalysisMutation() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (
      input:
        | { readonly action: "save"; readonly body: MonitorInput; readonly id?: string }
        | { readonly action: "delete" | "run" | "delete-report"; readonly id: string }
        | { readonly action: "clear-history"; readonly id: string; readonly createdBefore: string },
    ) => {
      if (input.action === "save") {
        if (input.id === undefined)
          return requireData(await api.POST("/ai-analysis/monitors", { body: input.body }));
        return requireData(
          await api.PUT("/ai-analysis/monitors/{monitorId}", {
            params: { path: { monitorId: input.id } },
            body: input.body,
          }),
        );
      }
      if (input.action === "delete-report")
        return requireData(
          await api.DELETE("/ai-analysis/reports/{reportId}", {
            params: { path: { reportId: input.id } },
          }),
        );
      if (input.action === "clear-history")
        return requireData(
          await api.DELETE("/ai-analysis/monitors/{monitorId}/reports", {
            params: { path: { monitorId: input.id } },
            body: { createdBefore: input.createdBefore },
          }),
        );
      if (input.action === "delete")
        return requireData(
          await api.DELETE("/ai-analysis/monitors/{monitorId}", {
            params: { path: { monitorId: input.id } },
          }),
        );
      return requireData(
        await api.POST("/ai-analysis/monitors/{monitorId}/run", {
          params: {
            path: { monitorId: input.id },
            header: { "x-idempotency-key": crypto.randomUUID() },
          },
        }),
      );
    },
    onSuccess: async (_, input) => {
      const deletingHistory = input.action === "delete-report" || input.action === "clear-history";
      if (deletingHistory) {
        // Cancel reads before purging deleted report content from the local cache.
        await client.cancelQueries({ queryKey: [...key, "report"] });
        client.removeQueries({ queryKey: [...key, "report"] });
      }
      await client.invalidateQueries({
        queryKey: key,
        predicate: (query) => !deletingHistory || query.queryKey[1] !== "report",
      });
    },
    retry: false,
  });
}
